import { toFixedClean } from '../utils';
import FilterService from '../services/filter';
import type { ProductRow } from '../services/state';
import { tracked } from '@glimmer/tracking';

export interface PPTSpurSequenceValue {
  sequenceName: string | null;
  sequenceFrequency: string | null;
  sequenceAmplitude: string | null;
  hasPPTSpur: boolean;
}

export default class PPTSpurModel {
  @tracked selectedProduct: ProductRow;
  @tracked filter?: FilterService;
  @tracked ftwBinary: string;
  @tracked systemClock: number;

  constructor(selectedProduct: ProductRow, systemClock: number, ftwBinary: string, filter?: FilterService) {
    this.selectedProduct = selectedProduct;
    this.systemClock = systemClock;
    this.ftwBinary = ftwBinary;
    if (filter) {
      this.filter = filter;
    }
  }

  get PPTSpur1(): PPTSpurSequenceValue {
    const pptSpur1: PPTSpurSequenceValue = {
      sequenceName: 'PPT Spur',
      sequenceFrequency: this.hasSpur ? toFixedClean(this.fppt1, 6) : null,
      sequenceAmplitude: this.hasSpur
        ? toFixedClean(this.pptMag, 6).replace('-Infinity', '0').replace('NaN', '')
        : null,
      hasPPTSpur: this.fppt1 !== 0 ? true : false,
    };
    return this.applyFilter(pptSpur1);
  }
  get PPTSpur2(): PPTSpurSequenceValue {
    const pptSpur2: PPTSpurSequenceValue = {
      sequenceName: 'PPT Spur',
      sequenceFrequency: this.hasSpur ? toFixedClean(this.fppt2, 6) : null,
      sequenceAmplitude: this.hasSpur
        ? toFixedClean(this.pptMag, 6).replace('-Infinity', '0').replace('NaN', '')
        : null,
      hasPPTSpur: this.fppt1 !== 0 ? true : false,
    };
    return this.applyFilter(pptSpur2);
  }

  /**
   * Computes for the index of the first instance of 1 starting from the rightmost character
   */
  get firstOneFromRight(): number {
    let result = 0;
    if (this.ftwBinary && this.ftwBinary.length > 0) {
      for (let i = this.ftwBinary.length; i > 0; i--) {
        result++;
        if (this.ftwBinary[i - 1] === '1') {
          break;
        }
      }
    }
    return result;
  }
  get lValue(): number {
    return this.selectedProduct.tuningWordWidth - this.firstOneFromRight + 1;
  }
  get pValue(): number {
    return this.selectedProduct.tuningWordWidth - this.selectedProduct.discardedBits;
  }
  get kValue(): number {
    const ftwBin = this.ftwBinary || '0';
    return parseInt(ftwBin.substring(0, ftwBin.length - this.firstOneFromRight + 1), 2);
  }
  get hasSpur(): boolean {
    return this.lValue > this.pValue;
  }
  get fppt1(): number {
    const r1 = (this.kValue * (Math.pow(2, this.pValue) - 1)) % Math.pow(2, this.lValue);
    return this.hasSpur ? (this.systemClock * r1) / Math.pow(2, this.lValue) : 0;
  }
  get fppt2(): number {
    const r2 = (this.kValue * (Math.pow(2, this.lValue) - Math.pow(2, this.pValue) + 1)) % Math.pow(2, this.lValue);
    return this.hasSpur ? (this.systemClock * r2) / Math.pow(2, this.lValue) : 0;
  }
  get pptMag(): number {
    const numerator = Math.sin(Math.PI / Math.pow(2, this.lValue));
    const denominator = Math.sin((Math.PI * (Math.pow(2, this.pValue) - 1)) / Math.pow(2, this.lValue));
    return this.hasSpur ? 20 * (Math.log(numerator / denominator) / Math.log(10)) : 0;
  }
  applyFilter(spur: PPTSpurSequenceValue): PPTSpurSequenceValue {
    if (this.filter) {
      const filterValue = this.filter.applyFilter(Number(spur.sequenceFrequency));
      if (filterValue.filterAmplitude) {
        spur.sequenceAmplitude = (
          (spur.sequenceAmplitude ? parseFloat(spur.sequenceAmplitude) : 0) + filterValue.filterAmplitude
        ).toFixed(6);
      }
    }
    return spur;
  }
}
